import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api/ApplicationApi';
import { getEndCustomerQuery } from 'api/queries/EndCustomerQueries';
import { PageButtons } from 'library/components/controls/buttons/PageButtons';
import { useAdobeTracking } from 'library/hooks/useAdobeTracking';

import { NewCustomerForm } from './NewCustomerForm';
import { EndCustomerWithLanguageDto } from './types';
import { useUpdateCustomerFormSubmit } from './useUpdateCustomerFormSubmit';

type EditCustomerDialogProps = {
	customer: EndCustomerWithLanguageDto;
	onClose: (customer: EndCustomerWithLanguageDto | null) => void;
	source?: string;
};

export const EditCustomerDialog = ({
	customer,
	onClose,
	source = 'unknown',
}: EditCustomerDialogProps) => {
	const { api } = useProductCenterApi();
	const { trackFormView, trackFormSubmitSuccess } = useAdobeTracking();
	const { data } = useQuery({
		...getEndCustomerQuery(api, customer.id),
		enabled: !!customer,
	});

	const { t } = useTranslation(['customer', 'commonButton']);
	const { enqueueSnackbar } = useSnackbar();

	const fullScreen = useMediaQuery((theme: Theme) =>
		theme.breakpoints.only('xs')
	);

	const methods = useForm<EndCustomerWithLanguageDto>({
		mode: 'onBlur',
	});

	const hasTrackedFormView = useRef(false);
	useEffect(() => {
		if (customer && !hasTrackedFormView.current) {
			hasTrackedFormView.current = true;
			void trackFormView({
				name: `Edit End Customer - ${source}`,
				type: 'end-customer',
				destination: source,
			});
		}
	}, [customer, trackFormView, source]);

	useEffect(() => {
		if (data) {
			methods.reset(data);
		}
	}, [data, methods]);

	const { handleSubmit, setError } = methods;

	const { handleFormSubmit, handleFormInvalid, customerMutation } =
		useUpdateCustomerFormSubmit({
			onSuccess: (result) => {
				void trackFormSubmitSuccess({
					type: 'end-customer',
					name: `Edit End Customer - ${source}`,
					id: result?.id?.toString() ?? '',
					destination: source,
					topic: 'edit',
				});
				enqueueSnackbar(
					t('customer:customer-edit-successful-message'),
					{ variant: 'success' }
				);
				onClose(result);
			},
			setError,
		});

	const handleClose = () => {
		onClose(null);
	};

	return (
		<Dialog
			fullScreen={fullScreen}
			fullWidth
			maxWidth={false}
			open={customer != null}
			onClose={handleClose}>
			<form
				onSubmit={handleSubmit(handleFormSubmit, handleFormInvalid)}
				noValidate>
				<DialogTitle>{t('customer:edit-customer')}</DialogTitle>
				<DialogContent>
					<FormProvider {...methods}>
						<NewCustomerForm withNotificationLanguage={false} />
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<PageButtons
						primaryButtonLabel={t('commonButton:save')}
						secondaryButtonLabel={t('commonButton:cancel')}
						secondaryButtonAction={handleClose}
						isPrimaryButtonSubmitType
						displayLoadingIndicator={customerMutation.isPending}
					/>
				</DialogActions>
			</form>
		</Dialog>
	);
};
